import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, Download, CheckCircle2, ShieldCheck, Settings, Layers, Wrench } from "lucide-react";
import { toast } from "sonner";
import { PageHero } from "../components/PageHero";
import { Reveal, Kicker } from "../components/Reveal";
import { PRODUCTS, SPEC_ROWS } from "../data/site";

const SECTIONS = (p) => [
    {
        icon: Layers, title: "Available Configurations",
        body: "Chamber size, temperature range, heating zones and loading arrangements are configured to the application. Detailed configuration options are defined during engineering.",
    },
    {
        icon: Settings, title: "Control & Automation",
        body: "PLC / HMI / PID based control with recipe management, temperature profiling, alarms and data logging — specified to match the process requirement.",
    },
    {
        icon: ShieldCheck, title: "Safety Features",
        body: "Over-temperature protection, safety interlocks and electrical protection engineered into every system. Final safety scope is defined per project.",
    },
    {
        icon: Wrench, title: "Optional Accessories",
        body: "Loading systems, atmosphere options, recorders, spare heating elements and instrumentation packages available as applicable to the configuration.",
    },
];

const ProductDetail = () => {
    const { slug } = useParams();
    const product = PRODUCTS.find((p) => p.slug === slug);
    if (!product) return <Navigate to="/products" replace />;

    const downloadDatasheet = () => {
        const lines = [
            "SS ENGINEERING WORKS — Industrial Furnace Solutions",
            `TECHNICAL DATASHEET: ${product.name.toUpperCase()}`,
            "",
            product.desc,
            "",
            "TECHNICAL SPECIFICATIONS",
            ...SPEC_ROWS.map(([k, v]) => `${k}: ${v}`),
            "",
            "For detailed specifications, contact:",
            "SS Engineering Works, Greater Noida, Uttar Pradesh, India",
            "Phone: +91 9811273588 / +91 8375019925",
            "Email: ssengineeringworks643@gmail.com",
        ].join("\n");
        const blob = new Blob([lines], { type: "text/plain" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `SSEW-${product.slug}-datasheet.txt`;
        a.click();
        URL.revokeObjectURL(a.href);
        toast.success("Datasheet downloaded");
    };

    return (
        <div data-testid="product-detail-page">
            <PageHero
                kicker={product.tag}
                title={product.name}
                sub={product.desc}
                image={product.image}
                crumb={product.name}
            />
            <section className="bg-white py-16 md:py-24">
                <div className="mx-auto grid max-w-[1440px] gap-14 px-4 sm:px-6 lg:grid-cols-5 lg:px-10">
                    <div className="lg:col-span-3">
                        <Reveal><Kicker>OVERVIEW</Kicker></Reveal>
                        <Reveal delay={0.1}>
                            <p className="mt-5 text-base leading-relaxed text-slate-600">
                                {product.name} from SS Engineering Works are engineered and manufactured to order. Each system is configured around the process — required temperature, working chamber, cycle and throughput — with electrical and control engineering integrated from the start.
                            </p>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <h2 className="mt-12 font-display text-xl font-bold uppercase tracking-tight text-brand-charcoal">Key Applications</h2>
                            <div className="mt-5 flex flex-wrap gap-2.5">
                                {product.apps.map((a) => (
                                    <span key={a} className="border border-slate-200 bg-brand-mist px-4 py-2 text-xs font-medium text-slate-600">{a}</span>
                                ))}
                            </div>
                        </Reveal>
                        <Reveal delay={0.2}>
                            <h2 className="mt-12 font-display text-xl font-bold uppercase tracking-tight text-brand-charcoal">Key Features</h2>
                            <ul className="mt-5 grid gap-3 sm:grid-cols-2">
                                {product.features.map((f) => (
                                    <li key={f} className="flex items-start gap-3 text-sm text-slate-600">
                                        <CheckCircle2 size={17} className="mt-0.5 shrink-0 text-brand-red" /> {f}
                                    </li>
                                ))}
                            </ul>
                        </Reveal>
                        <div className="mt-12 grid gap-px bg-slate-200 sm:grid-cols-2">
                            {SECTIONS(product).map((s, i) => (
                                <Reveal key={s.title} delay={i * 0.06}>
                                    <div className="h-full bg-white p-6">
                                        <s.icon size={22} strokeWidth={1.5} className="text-brand-red" />
                                        <p className="mt-4 font-display text-base font-bold uppercase text-brand-charcoal">{s.title}</p>
                                        <p className="mt-2 text-sm leading-relaxed text-slate-500">{s.body}</p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                        <Reveal delay={0.1}>
                            <div className="mt-12 border border-slate-200 bg-brand-mist p-6">
                                <h2 className="font-display text-base font-bold uppercase text-brand-charcoal">Installation & Commissioning</h2>
                                <p className="mt-2 text-sm leading-relaxed text-slate-500">
                                    Installation, testing and commissioning support is provided as part of the project scope — including electrical checks, control verification and heat-up trials as applicable.
                                </p>
                            </div>
                        </Reveal>
                    </div>
                    <aside className="lg:col-span-2">
                        <Reveal delay={0.1} className="lg:sticky lg:top-28">
                            <div className="overflow-hidden">
                                <img src={product.image} alt={`${product.name} detail`} loading="lazy" className="h-64 w-full object-cover" />
                            </div>
                            <div className="mt-6 border border-slate-200" data-testid="spec-table">
                                <div className="bg-brand-ink px-6 py-4">
                                    <p className="font-display text-sm font-bold tracking-[0.16em] text-white uppercase">Technical Specifications</p>
                                </div>
                                <table className="w-full text-sm">
                                    <tbody>
                                        {SPEC_ROWS.map(([k, v], i) => (
                                            <tr key={k} className={i % 2 ? "bg-brand-mist" : "bg-white"}>
                                                <td className="px-6 py-3.5 font-semibold text-brand-charcoal align-top">{k}</td>
                                                <td className="px-6 py-3.5 text-slate-500">{v}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <p className="border-t border-slate-200 px-6 py-3 font-mono text-[10px] tracking-[0.14em] text-slate-400 uppercase">
                                    Specifications available on request
                                </p>
                            </div>
                            <div className="mt-6 flex flex-col gap-3">
                                <button onClick={downloadDatasheet} data-testid="download-datasheet-btn"
                                    className="flex items-center justify-center gap-2 border border-brand-charcoal px-6 py-3.5 font-display text-xs font-bold tracking-[0.14em] text-brand-charcoal transition-colors hover:bg-brand-charcoal hover:text-white">
                                    <Download size={15} /> DOWNLOAD TECHNICAL DATASHEET
                                </button>
                                <Link to={`/get-a-quote?product=${product.slug}`} data-testid="detail-request-quote"
                                    className="group flex items-center justify-center gap-2 bg-brand-red px-6 py-3.5 font-display text-xs font-bold tracking-[0.14em] text-white transition-colors hover:bg-brand-charcoal">
                                    REQUEST QUOTATION <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                                </Link>
                            </div>
                        </Reveal>
                    </aside>
                </div>
            </section>
        </div>
    );
};

export default ProductDetail;
